import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CloudUploadOutlined,
  EditOutlined,
  EyeOutlined,
  FileAddOutlined,
  FileTextOutlined,
  FormOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  PushpinFilled,
  PushpinOutlined,
  RocketOutlined,
  SearchOutlined,
  ThunderboltOutlined,
  UserOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Input, Modal, Space, Spin, Table, Tabs, Tag, Tooltip, Typography, message } from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import { UserContext } from "@/Context/UserContext";

const { Title, Text } = Typography;

const Dashboard = () => {
  const navigate = useNavigate();
  const fetch = useAuthenticatedFetch();
  const { userProfileData, updateProfileInfo } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [resumesData, setResumesData] = useState([]);
  const [resumeData, setResumeData] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [pinnedResumes, setPinnedResumes] = useState(() => {
    const saved = localStorage.getItem("pinnedResumes");
    return saved ? JSON.parse(saved) : [];
  });
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [planInfo, setPlanInfo] = useState({
    plan: "free",
    currentResumeCount: 0,
    credits: {
      balance: 30,
      totalPurchased: 0,
      totalUsed: 0,
      freeCreditsGiven: 30,
    },
    featureCosts: {
      resume_parsing: 10,
      resume_scoring: 10,
      blank_resume: 10,
      resume_from_job_post: 15,
      ai_enhance: 5,
      cover_letter: 10,
    },
    featureNames: {
      resume_parsing: "Resume Parsing",
      resume_scoring: "Resume Scoring",
      blank_resume: "Blank Resume",
      resume_from_job_post: "Resume from Job Post",
      ai_enhance: "AI Enhancement",
      cover_letter: "Cover Letter",
    },
    canCreateResume: true,
    canEnhance: true,
    canGenerateCoverLetter: true,
    canScoreResume: true,
    // Legacy fields
    maxResumes: 999,
    totalCredits: 15,
    usedCredits: 0,
  });

  // Computed credit balance - use this for all credit checks
  const creditBalance = planInfo.credits?.balance ?? planInfo.totalCredits ?? 15;
  const isCreditsEmpty = creditBalance <= 2; // Show "Add Credits" when 3 or fewer credits remain

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem("userId");

      if (!userId) {
        navigate("/app/login");
        return;
      }

      // Get user data with resume information
      const response = await fetch.get(`/user/dashboard/${userId}`);

      if (response.success) {
        setUserData(response.data.user);

        // Handle both old (resume) and new (resumes array) API response
        const resumes = response.data.resumes || (response.data.resume ? [response.data.resume] : []);
        setResumesData(resumes);

        // Set first resume as primary for backward compatibility
        const primaryResume = resumes.length > 0 ? resumes[0] : null;
        setResumeData(primaryResume);

        // Store plan info from API response - include user credits data
        const userCredits = response.data.user?.credits || {};
        const creditBalance = userCredits.balance ?? response.data.user?.totalCredits ?? 15;

        if (response.data.planInfo) {
          // Merge API planInfo with credits from user
          setPlanInfo({
            ...response.data.planInfo,
            credits: response.data.planInfo.credits || {
              balance: creditBalance,
              totalPurchased: userCredits.totalPurchased ?? 0,
              totalUsed: userCredits.totalUsed ?? 0,
              freeCreditsGiven: userCredits.freeCreditsGiven ?? 30,
            },
          });
        } else {
          // Fallback: calculate from user data
          const resumeCount = resumes.length;
          const maxResumes = response.data.user?.maxResumes || 3;
          setPlanInfo({
            plan: response.data.user?.plan || "free",
            maxResumes,
            currentResumeCount: resumeCount,
            canCreateResume: creditBalance >= 10,
            credits: {
              balance: creditBalance,
              totalPurchased: userCredits.totalPurchased ?? 0,
              totalUsed: userCredits.totalUsed ?? 0,
              freeCreditsGiven: userCredits.freeCreditsGiven ?? 30,
            },
            featureCosts: {
              resume_parsing: 10,
              resume_scoring: 10,
              blank_resume: 10,
              resume_from_job_post: 15,
              ai_enhance: 5,
              cover_letter: 10,
            },
            featureNames: {
              resume_parsing: "Resume Parsing",
              resume_scoring: "Resume Scoring",
              blank_resume: "Blank Resume",
              resume_from_job_post: "Resume from Job Post",
              ai_enhance: "AI Enhancement",
              cover_letter: "Cover Letter",
            },
          });
        }

        // Sync resumeId to UserContext so sidebar works correctly
        const newResumeId = primaryResume?._id || null;
        if (userProfileData?.resumeId !== newResumeId) {
          updateProfileInfo({
            ...userProfileData,
            resumeId: newResumeId,
          });
        }
      }
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditResume = (resume) => {
    if (resume) {
      navigate(`/app/resume/${resume._id}`);
    }
  };

  const handleCreateResume = () => {
    if (!planInfo.canCreateResume) {
      message.warning("You have reached your resume limit");
      return;
    }
    setCreateModalVisible(true);
  };

  const handleCreateOption = (option) => {
    setCreateModalVisible(false);
    switch (option) {
      case "five-second":
        navigate("/app/resume-score");
        break;
      case "naukri":
        navigate("/app/naukri-job-input");
        break;
      case "enhance":
        navigate("/app/resume-upload");
        break;
      case "scratch":
        navigate("/app/resume-setup");
        break;
      default:
        break;
    }
  };

  const createOptions = [
    {
      key: "enhance",
      icon: <CloudUploadOutlined />,
      title: "Enhance Existing Resume",
      description: "Upload & improve with AI",
      color: "#1890ff",
      bgColor: "#e6f7ff",
      highlighted: false,
    },
    {
      key: "scratch",
      icon: <FileAddOutlined />,
      title: "Create from Scratch",
      description: "Build step by step",
      color: "#52c41a",
      bgColor: "#f6ffed",
      highlighted: false,
    },
    {
      key: "naukri",
      icon: <RocketOutlined />,
      title: "Naukri Job Post Match",
      description: "Cleaned & structured job data ready for AI",
      color: "#fa541c",
      bgColor: "#fff2e8",
      highlighted: true,
    },
    {
      key: "five-second",
      icon: <ThunderboltOutlined />,
      title: "5 Sec Resume",
      description: "Instant ATS score",
      color: "#722ed1",
      bgColor: "#f9f0ff",
      highlighted: true,
    },
  ];

  const handleViewResume = (resume) => {
    if (resume) {
      navigate(`/app/resume/${resume._id}/preview`);
    }
  };

  const handlePinResume = (resumeId) => {
    setPinnedResumes((prev) => {
      const newPinned = prev.includes(resumeId)
        ? prev.filter((id) => id !== resumeId)
        : [...prev, resumeId];
      localStorage.setItem("pinnedResumes", JSON.stringify(newPinned));
      return newPinned;
    });
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes} minutes ago`;
    if (hours < 24) return `${hours} hours ago`;
    if (days < 7) return `${days} days ago`;
    return new Date(date).toLocaleDateString();
  };

  const getResumeSource = (item) => {
    if (item.documentType === "cover-letter") {
      return { label: "Cover Letter", color: "magenta" };
    }
    // Determine source: use resumeSource field first, fallback to legacy detection
    const source = item.resumeSource
      || (item.creationMode === "blank" && item.initialContext?.source === "naukri-job-input" ? "naukri" : null)
      || (item.creationMode === "upload" ? "upload" : null)
      || (item.creationMode === "blank" ? "blank" : "upload");

    const sourceMap = {
      naukri: { label: "Naukri", color: "cyan" },
      "five-sec-score": { label: "5 Sec Resume", color: "purple" },
      upload: { label: "Upload File", color: "blue" },
      blank: { label: "Generate Resume", color: "green" },
    };
    return sourceMap[source] || { label: "Unknown", color: "default" };
  };

  // Extract cover letters from resumes
  const coverLettersData = resumesData
    .filter((resume) => resume.coverLetter?.generatedContent)
    .map((resume) => {
      const companyName = resume.coverLetter?.companyName;
      const targetRole = resume.coverLetter?.targetRole;
      let name = "Cover Letter";
      if (companyName && targetRole) {
        name = `Cover Letter - ${companyName} / ${targetRole}`;
      } else if (companyName) {
        name = `Cover Letter - ${companyName}`;
      } else if (targetRole) {
        name = `Cover Letter - ${targetRole}`;
      } else {
        name = `Cover Letter - ${resume.parsedData?.personalInfo?.name || "Untitled"}`;
      }
      return {
        _id: `cl-${resume._id}`,
        resumeId: resume._id,
        documentType: "cover-letter",
        name,
        targetRole,
        companyName,
        createdAt: resume.coverLetter?.createdAt || resume.createdAt,
        updatedAt: resume.coverLetter?.lastUpdated || resume.updatedAt,
        parsedData: resume.parsedData,
      };
    });

  // Combine all documents
  const allDocuments = [
    ...resumesData.map((r) => ({ ...r, documentType: "resume" })),
    ...coverLettersData,
  ];

  // Filter data based on active tab
  const getFilteredData = () => {
    let data = [];

    if (activeTab === "all") {
      // Show all documents (resumes + cover letters)
      data = allDocuments;
    } else if (activeTab === "resumes") {
      // Show only resumes
      data = resumesData.map((r) => ({ ...r, documentType: "resume" }));
    } else if (activeTab === "cover-letters") {
      // Show only cover letters
      data = coverLettersData;
    }

    // Apply search filter
    return data.filter((item) => {
      const name = item.documentType === "cover-letter"
        ? item.name || item.companyName || ""
        : item.parsedData?.personalInfo?.name || "";
      return name.toLowerCase().includes(searchText.toLowerCase());
    });
  };

  const filteredResumes = getFilteredData()
    .sort((a, b) => {
      // Sort pinned resumes to top
      const aPinned = pinnedResumes.includes(a._id);
      const bPinned = pinnedResumes.includes(b._id);
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      return 0;
    });

  const handleEditCoverLetter = (coverLetter) => {
    if (coverLetter?.resumeId) {
      navigate(`/app/resume/${coverLetter.resumeId}?tab=cover-letter`);
    }
  };

  const handleViewCoverLetter = (coverLetter) => {
    if (coverLetter?.resumeId) {
      navigate(`/app/resume/${coverLetter.resumeId}/preview?tab=cover-letter`);
    }
  };

  const columns = [
    {
      title: "",
      dataIndex: "pin",
      key: "pin",
      width: 50,
      render: (_, record) => {
        const isPinned = pinnedResumes.includes(record._id);
        return (
          <Tooltip title={isPinned ? "Unpin" : "Pin to top"}>
            {isPinned ? (
              <PushpinFilled
                className="pin-icon pinned"
                onClick={() => handlePinResume(record._id)}
              />
            ) : (
              <PushpinOutlined
                className="pin-icon"
                onClick={() => handlePinResume(record._id)}
              />
            )}
          </Tooltip>
        );
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_, record) => {
        const isCoverLetter = record.documentType === "cover-letter";
        const name = isCoverLetter
          ? record.name || `Cover Letter - ${record.companyName || "Untitled"}`
          : record.parsedData?.personalInfo?.name || "Untitled Resume";
        const handleClick = isCoverLetter ? () => handleEditCoverLetter(record) : () => handleEditResume(record);

        return (
          <div className="resume-name-cell">
            {isCoverLetter ? (
              <FormOutlined className="resume-icon cover-letter-icon" />
            ) : (
              <FileTextOutlined className="resume-icon" />
            )}
            <span className="resume-name" onClick={handleClick}>
              {name}
            </span>
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 160,
      render: (_, record) => {
        const source = getResumeSource(record);
        return <Tag color={source.color}>{source.label}</Tag>;
      },
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
    },
    {
      title: "Modified",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 140,
      render: (date) => getTimeAgo(date),
    },
    {
      title: "",
      key: "actions",
      width: 100,
      render: (_, record) => {
        const isCoverLetter = record.documentType === "cover-letter";
        return (
          <Space size="small" className="action-buttons">
            <Tooltip title="Preview">
              <Button
                type="text"
                icon={<EyeOutlined />}
                className="action-btn"
                onClick={() => isCoverLetter ? handleViewCoverLetter(record) : handleViewResume(record)}
              />
            </Tooltip>
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditOutlined />}
                className="action-btn"
                onClick={() => isCoverLetter ? handleEditCoverLetter(record) : handleEditResume(record)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const tabItems = [
    {
      key: "all",
      label: `All documents (${allDocuments.length})`,
    },
    {
      key: "resumes",
      label: `Resumes (${resumesData.length})`,
    },
    {
      key: "cover-letters",
      label: `Cover letters (${coverLettersData.length})`,
    },
  ];

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <style>{`
        .dashboard-container {
          max-width: 100%;
          margin: 0;
          padding: 40px 60px 80px;
          min-height: 100vh;
          background-color: #ffffff;
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
          padding-bottom: 24px;
          border-bottom: 1px solid #f0f0f0;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .user-avatar {
          background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%) !important;
          font-size: 22px;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
        }

        .welcome-text {
          display: flex;
          flex-direction: column;
        }

        .welcome-title {
          margin: 0 !important;
          font-size: 28px !important;
          font-weight: 700 !important;
          color: #262626;
          -webkit-text-fill-color: #262626;
        }

        .user-name-highlight {
          background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: 800;
        }

        .welcome-subtitle {
          font-size: 16px;
          color: #595959;
          margin-top: 6px;
          font-weight: 500;
          letter-spacing: 0.3px;
        }

        .plan-info {
          font-size: 15px;
          color: #595959;
          margin-top: 4px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .plan-info .purchased-plan-badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 12px;
          background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
          border-radius: 16px;
          font-size: 13px;
          color: #52c41a;
          font-weight: 600;
          box-shadow: 0 2px 4px rgba(82, 196, 26, 0.15);
        }

        .plan-info .purchased-plan-badge.free {
          background: linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%);
          color: #722ed1;
          box-shadow: 0 2px 4px rgba(114, 46, 209, 0.15);
        }

        .plan-info .plan-badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 12px;
          background: linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%);
          border-radius: 16px;
          font-size: 13px;
          color: #1890ff;
          font-weight: 600;
          box-shadow: 0 2px 4px rgba(24, 144, 255, 0.15);
        }

        .plan-info .plan-badge.empty {
          background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%) !important;
          color: #ffffff !important;
          border: none;
          box-shadow: 0 2px 8px rgba(24, 144, 255, 0.4) !important;
        }

        .plan-info .limit-badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 12px;
          background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
          border-radius: 16px;
          font-size: 13px;
          color: #ffffff;
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(24, 144, 255, 0.4);
          animation: pulse 2s infinite;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .plan-info .limit-badge:hover {
          background: linear-gradient(135deg, #40a9ff 0%, #69c0ff 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(24, 144, 255, 0.5);
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }

        .zero-credits-banner {
          background: #f0f5ff;
          border: 1px solid #d6e4ff;
          border-radius: 8px;
          padding: 10px 16px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .zero-credits-banner .banner-text {
          font-size: 14px;
          color: #1d39c4;
          font-weight: 500;
        }

        .zero-credits-banner .banner-btn {
          background: #1890ff !important;
          border: none !important;
          color: white !important;
          font-weight: 500 !important;
          height: 34px !important;
          padding: 0 16px !important;
          border-radius: 6px !important;
          font-size: 13px !important;
          white-space: nowrap !important;
        }

        .zero-credits-banner .banner-btn:hover {
          background: #40a9ff !important;
        }

        .create-btn {
          height: 44px !important;
          padding: 0 24px !important;
          font-size: 15px !important;
          font-weight: 500 !important;
          border-radius: 8px !important;
          background: #1890ff !important;
          border-color: #1890ff !important;
          box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3) !important;
          transition: all 0.3s ease !important;
        }

        .create-btn:hover:not(:disabled) {
          background: #40a9ff !important;
          border-color: #40a9ff !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4) !important;
        }

        .create-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .dashboard-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .dashboard-tabs {
          flex: 1;
        }

        .dashboard-tabs .ant-tabs-nav::before {
          border-bottom: none;
        }

        .dashboard-tabs .ant-tabs-nav-list {
          gap: 40px;
        }

        .dashboard-tabs .ant-tabs-tab {
          padding: 12px 0;
          margin: 0 !important;
          font-size: 15px;
          color: #8c8c8c;
          transition: color 0.3s ease;
        }

        .dashboard-tabs .ant-tabs-tab:hover {
          color: #1890ff;
        }

        .dashboard-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #1890ff !important;
          font-weight: 500;
        }

        .dashboard-tabs .ant-tabs-ink-bar {
          background: #1890ff !important;
          height: 3px;
          border-radius: 2px;
        }

        .toolbar-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .search-input {
          width: 240px;
          height: 40px;
          border-radius: 8px;
          border: 1px solid #f0f0f0;
          transition: all 0.3s ease;
        }

        .search-input:hover,
        .search-input:focus-within {
          border-color: #1890ff;
          box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
        }

        .search-input .ant-input-prefix {
          color: #8c8c8c;
          margin-right: 8px;
        }

        .documents-table-wrapper {
          background: #ffffff;
          border-radius: 12px;
          border: 1px solid #f0f0f0;
          overflow: hidden;
        }

        .documents-table .ant-table-thead > tr > th {
          background: #fafafa;
          font-weight: 500;
          font-size: 13px;
          color: #8c8c8c;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 1px solid #f0f0f0;
          padding: 14px 16px;
        }

        .documents-table .ant-table-tbody > tr {
          transition: background 0.2s ease;
        }

        .documents-table .ant-table-tbody > tr:hover > td {
          background: #e6f7ff !important;
        }

        .documents-table .ant-table-tbody > tr > td {
          padding: 16px;
          border-bottom: 1px solid #f0f0f0;
          vertical-align: middle;
        }

        .resume-name-cell {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .resume-icon {
          font-size: 20px;
          color: #1890ff;
          background: #e6f7ff;
          padding: 8px;
          border-radius: 8px;
        }

        .resume-icon.cover-letter-icon {
          color: #eb2f96;
          background: #fff0f6;
        }

        .resume-name {
          font-weight: 500;
          font-size: 15px;
          color: #262626;
          cursor: pointer;
          transition: color 0.2s ease;
        }

        .resume-name:hover {
          color: #1890ff;
        }

        .pin-icon {
          font-size: 18px;
          color: #d9d9d9;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .pin-icon:hover {
          color: #faad14;
          transform: scale(1.1);
        }

        .pin-icon.pinned {
          color: #faad14;
        }

        .action-buttons {
          opacity: 1;
        }

        .action-btn {
          width: 34px !important;
          height: 34px !important;
          border-radius: 6px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          color: #595959 !important;
          font-size: 16px !important;
          transition: all 0.2s ease !important;
        }

        .action-btn:hover {
          color: #1890ff !important;
          background: #e6f7ff !important;
        }

        .action-btn .anticon {
          font-size: 16px !important;
        }

        .empty-state {
          text-align: center;
          padding: 80px 24px;
        }

        .empty-icon {
          font-size: 64px;
          color: #f0f0f0;
          margin-bottom: 24px;
        }

        .empty-title {
          margin: 0 0 8px 0 !important;
          color: #8c8c8c !important;
          font-weight: 500 !important;
        }

        .empty-text {
          display: block;
          margin-bottom: 24px;
          font-size: 15px;
        }

        .empty-create-btn {
          height: 48px !important;
          padding: 0 32px !important;
          font-size: 16px !important;
          font-weight: 500 !important;
          border-radius: 8px !important;
          background: #1890ff !important;
          border-color: #1890ff !important;
          box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3) !important;
        }

        .empty-create-btn:hover {
          background: #40a9ff !important;
          border-color: #40a9ff !important;
          transform: translateY(-1px);
        }

        @media (max-width: 768px) {
          .dashboard-container {
            padding: 16px 16px 60px;
          }

          .dashboard-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }

          .header-right {
            width: 100%;
          }

          .create-btn {
            width: 100% !important;
          }

          .dashboard-toolbar {
            flex-direction: column;
            align-items: stretch;
          }

          .dashboard-tabs {
            order: 1;
          }

          .toolbar-right {
            order: 0;
            width: 100%;
            flex-wrap: wrap;
          }

          .search-input {
            flex: 1;
            min-width: 200px;
          }
        }

        @media (max-width: 480px) {
          .welcome-title {
            font-size: 20px !important;
          }

          .search-input {
            width: 100%;
          }
        }

        .create-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }

        .create-modal .ant-modal-header {
          padding: 24px 24px 16px;
          border-bottom: none;
        }

        .create-modal .ant-modal-title {
          font-size: 22px;
          font-weight: 700;
          color: #262626;
        }

        .create-modal .ant-modal-body {
          padding: 8px 24px 24px;
        }

        .create-options-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }

        .create-option-card {
          padding: 16px 12px;
          border-radius: 12px;
          border: 2px solid #f0f0f0;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 10px;
        }

        .create-option-card:hover {
          border-color: currentColor;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        }

        .create-option-card.highlighted {
          border-color: currentColor;
          background: linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
          position: relative;
        }

        .create-option-card.highlighted::before {
          content: "new";
          position: absolute;
          top: -8px;
          right: 8px;
          background: linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%);
          color: white;
          font-size: 10px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 10px;
          text-transform: uppercase;
        }

        .create-option-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
        }

        .create-option-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .create-option-title {
          font-size: 13px;
          font-weight: 600;
          color: #262626;
          margin: 0;
          line-height: 1.3;
        }

        .create-option-description {
          font-size: 11px;
          color: #8c8c8c;
          margin: 0;
          line-height: 1.3;
        }

        @media (max-width: 768px) {
          .create-options-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 480px) {
          .create-options-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      <div className="dashboard-container">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="header-left">
          <Avatar
            size={56}
            className="user-avatar"
            icon={userData?.firstName ? undefined : <UserOutlined />}
          >
            {userData?.firstName?.charAt(0)?.toUpperCase()}
          </Avatar>
          <div className="welcome-text">
            <Title level={2} className="welcome-title">
              Hey <span className="user-name-highlight">{userData?.firstName || "there"}</span> ! Ready to land your dream job? 
            </Title>
            <Text className="welcome-subtitle">
            Your career success starts here — Create, customize & conquer!
            </Text>
            <div className="plan-info">
              <span className={`plan-badge ${isCreditsEmpty ? "empty" : ""}`}>
                ⚡ {creditBalance} Credits
                <Tooltip
                  title={
                    <div style={{ padding: "8px 0" }}>
                      <div style={{ fontWeight: 600, marginBottom: "8px", fontSize: "13px" }}>Credit Usage Guide</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        {planInfo.featureCosts && Object.entries(planInfo.featureCosts).map(([key, cost]) => (
                          <div key={key} style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                            <span>{planInfo.featureNames?.[key] || key}:</span>
                            <span style={{ fontWeight: 500, marginLeft: "12px" }}>{cost} credits</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid rgba(255,255,255,0.2)", fontSize: "11px", color: "rgba(255,255,255,0.8)" }}>
                        Credits deducted on successful completion
                      </div>
                    </div>
                  }
                  placement="bottom"
                >
                  <InfoCircleOutlined style={{ marginLeft: "6px", fontSize: "12px", cursor: "help" }} />
                </Tooltip>
              </span>
              {isCreditsEmpty && (
                <span
                  className="limit-badge"
                  onClick={() => navigate("/app/account?tab=billing")}
                  style={{ cursor: "pointer" }}
                >
                  <WalletOutlined style={{ marginRight: "4px" }} />
                  Add Credits
                </span>
              )}
            </div>
          </div>
        </div>
        {resumesData.length > 0 && (
          <div className="header-right">
            <Tooltip
              title={
                !planInfo.canCreateResume
                  ? `Insufficient credits. Need ${planInfo.featureCosts?.resume_parsing || 10} credits to create resume.`
                  : ""
              }
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="large"
                className="create-btn"
                onClick={handleCreateResume}
                disabled={!planInfo.canCreateResume}
              >
                Create new
              </Button>
            </Tooltip>
          </div>
        )}
      </div>

      {/* Low Credits Banner */}
      {isCreditsEmpty && (
        <div className="zero-credits-banner">
          <span className="banner-text">
            {creditBalance === 0 ? "You're out of credits." : `Only ${creditBalance} credit${creditBalance > 1 ? 's' : ''} left.`}
          </span>
          <Button
            className="banner-btn"
            size="small"
            onClick={() => navigate("/app/account?tab=billing")}
          >
            Add Credits
          </Button>
        </div>
      )}

      {/* Tabs and Search Section */}
      <div className="dashboard-toolbar">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="dashboard-tabs"
        />
        <div className="toolbar-right">
          <Input
            placeholder="Search documents..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="search-input"
            allowClear
          />
        </div>
      </div>

      {/* Documents Table */}
      <div className="documents-table-wrapper">
        {filteredResumes.length > 0 ? (
          <Table
            columns={columns}
            dataSource={filteredResumes}
            rowKey="_id"
            pagination={false}
            className="documents-table"
          />
        ) : (
          <div className="empty-state">
            {activeTab === "cover-letters" ? (
              <FormOutlined className="empty-icon" />
            ) : (
              <FileTextOutlined className="empty-icon" />
            )}
            <Title level={4} className="empty-title">
              {searchText
                ? `No ${activeTab === "cover-letters" ? "cover letters" : "documents"} found`
                : activeTab === "cover-letters"
                ? "No cover letters yet"
                : "No resumes yet"}
            </Title>
            <Text type="secondary" className="empty-text">
              {searchText
                ? "Try a different search term"
                : activeTab === "cover-letters"
                ? "✨ Create a cover letter from any of your resumes"
                : planInfo.canCreateResume
                ? "🎯 Get started by creating your first professional resume"
                : "🎉 Wow! You've maxed out your resume game! Upgrade to keep the momentum going 🚀"}
            </Text>
            {!searchText && activeTab !== "cover-letters" && planInfo.canCreateResume && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="large"
                className="empty-create-btn"
                onClick={() => setCreateModalVisible(true)}
              >
                Create your first resume
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Create Resume Modal */}
      <Modal
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        footer={null}
        width={720}
        className="create-modal"
        title="Create New Document"
        centered
      >
        <div className="create-options-grid">
          {createOptions.map((option) => (
            <div
              key={option.key}
              className={`create-option-card ${option.highlighted ? "highlighted" : ""}`}
              style={{ color: option.color }}
              onClick={() => handleCreateOption(option.key)}
            >
              <div
                className="create-option-icon"
                style={{ backgroundColor: option.bgColor, color: option.color }}
              >
                {option.icon}
              </div>
              <div className="create-option-content">
                <h4 className="create-option-title">{option.title}</h4>
                <p className="create-option-description">{option.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
    </>
  );
};

export default Dashboard;
